import { isDateValid, isRelativeDateToken, parseDate } from '@ardoq/date-time';
import {
  isRelativeDateValue,
  type FormattingOperator,
  type QueryBuilderRuleValue,
} from '@ardoq/api-types';
import {
  DateValueKind,
  getDateValueKind,
  isBetweenValueValid,
} from '@ardoq/query-builder';

type ValueShape = {
  expected: string;
  isValid: (value: QueryBuilderRuleValue | undefined) => boolean;
};

const isDateString = (value: QueryBuilderRuleValue | undefined): boolean =>
  typeof value === 'string' && isDateValid(parseDate(value));

const isDateOrRelativeToken = (
  value: QueryBuilderRuleValue | undefined
): boolean =>
  isDateString(value) ||
  (typeof value === 'string' && isRelativeDateToken(value));

const isTwoDateStrings = (value: QueryBuilderRuleValue | undefined): boolean =>
  isBetweenValueValid(value) &&
  Array.isArray(value) &&
  value.every(isDateString);

const VALUE_SHAPE_BY_KIND: Record<
  Exclude<DateValueKind, DateValueKind.NONE>,
  ValueShape
> = {
  [DateValueKind.SINGLE]: {
    expected:
      'a single ISO date string such as "2024-01-31", or a day relative to today as "now+0d" (today), "now-1d" (yesterday) or "now+7d" (in a week)',
    isValid: isDateOrRelativeToken,
  },
  [DateValueKind.PAIR]: {
    expected:
      'an array of two ISO date strings, ["2024-01-01", "2024-12-31"] (start, end)',
    isValid: isTwoDateStrings,
  },
  [DateValueKind.RELATIVE]: {
    expected:
      'a relative-date object such as { "period": "this", "unit": "month" }',
    isValid: isRelativeDateValue,
  },
};

/** What a date comparator needs its value to be, or `null` when the value fits. */
export const describeExpectedDateValue = (
  comparator: FormattingOperator,
  value: QueryBuilderRuleValue | undefined
): string | null => {
  const kind = getDateValueKind(comparator);
  if (kind === null || kind === DateValueKind.NONE) {
    return null;
  }

  const shape = VALUE_SHAPE_BY_KIND[kind];

  return shape.isValid(value) ? null : shape.expected;
};
